import {
    defaultVisibleColumns,
    initialPortfolioSummaryConfiguration,
    PortfolioSummaryConfigSchemaVersion,
    PortfolioSummaryConfigurationState,
} from 'widgets/portfolioSummary/store/instances/configuration/PortfolioSummaryConfigurationState';
import { Action } from 'store/store';
import { WidgetSetConfigPayload } from 'widgets/store/configuration/actions';
import { WidgetActionType } from 'widgets/store/actions';
import { arraysDiffer } from 'utils/arrays/arrayUtils';
import { PortfolioSummaryColumn } from 'widgets/portfolioSummary/grid/portfolioSummaryGridColumns';
import isEqual from 'lodash/isEqual';
import without from 'lodash/without';
import { WidgetType } from 'widgets/WidgetType';
import { buildConfigReducerWithGridSupport } from 'widgets/store/grid/reducer';

function updateVersion(
    state: PortfolioSummaryConfigurationState = initialPortfolioSummaryConfiguration,
    action: Action<
        string,
        Partial<WidgetSetConfigPayload<PortfolioSummaryConfigurationState>>
    >
): PortfolioSummaryConfigurationState {
    switch (action.type) {
        case WidgetActionType.WIDGET_SET_CONFIG:
        case WidgetActionType.WIDGET_LOAD_CONFIG: {
            if (state.version === PortfolioSummaryConfigSchemaVersion.Latest) {
                // Already at latest => no upgrade required
                return state;
            }
            // Bump version...
            const nextState = {
                ...state,
                version: PortfolioSummaryConfigSchemaVersion.Latest,
            };

            // On a load from the layout we may need to upgrade visible columns...
            if (action.type === WidgetActionType.WIDGET_LOAD_CONFIG) {
                nextState.visibleColumns = upgradeVisibleColumns(
                    action.payload!.configDelta?.visibleColumns || [],
                    state.version
                );
            }
            return nextState;
        }
    }

    return state;
}

export const oldVisibleColumnVersions: Partial<
    Record<PortfolioSummaryConfigSchemaVersion, PortfolioSummaryColumn[]>
> = {
    [PortfolioSummaryConfigSchemaVersion.Initial]: [
        'groupName',
        'count',
        'orderQty',
        'tradedQty',
        'leavesQty',
        'orderedValueLoc',
        'tradedValueLoc',
        'leavesValueLoc',
        'averagePercentageCompletion',
    ],
};

function upgradeVisibleColumns(
    oldVisibleColumns: PortfolioSummaryColumn[],
    version?: PortfolioSummaryConfigSchemaVersion
): PortfolioSummaryColumn[] {
    if (oldVisibleColumns.length === 0) {
        return defaultVisibleColumns;
    }

    const currentVersion =
        version === undefined
            ? PortfolioSummaryConfigSchemaVersion.Initial
            : version;

    if (currentVersion === PortfolioSummaryConfigSchemaVersion.Latest) {
        return oldVisibleColumns;
    }

    const previousVersionDefaultVisibleColumns =
        oldVisibleColumnVersions[currentVersion] ?? defaultVisibleColumns;

    if (isEqual(oldVisibleColumns, previousVersionDefaultVisibleColumns)) {
        return defaultVisibleColumns;
    }

    if (
        !arraysDiffer(oldVisibleColumns, previousVersionDefaultVisibleColumns)
    ) {
        // Using previous version default visible columns but re-ordered, so stick the new ones at the end
        const newCols = without(
            defaultVisibleColumns,
            ...previousVersionDefaultVisibleColumns
        );
        return [...oldVisibleColumns, ...newCols];
    }
    return oldVisibleColumns;
}

export const portfolioSummaryConfigReducer = buildConfigReducerWithGridSupport(
    WidgetType.PORTFOLIO_SUMMARY,
    initialPortfolioSummaryConfiguration,
    { postReducer: updateVersion }
);
